import {
  useGetAdmissionsPatient,
  useGetCareProviderAdmissionsPage,
  useGetCareproviderFileList,
} from "apollo/hooks/queries";
import { isLoading, Result } from "apollo/utils";
import { ADMISSIONS_STEPS, FILE_TYPES } from "core/consts";
import { useIntParams } from "core/model/utils/urls";
import {
  AdmissionsFormType,
  Auction,
  Careprovider,
  CareproviderAdmissionPage,
} from "core/types";
import SpinnerPage, {
  SpinnerPageContainer,
} from "ds_legacy/components/Spinner";
import { useCareproviderNavigationHandlers } from "dsl/hooks";
import { PatientEncryptionProvider } from "dsl/organisms/Encryption/Patient";
import { createContext, useCallback, useContext } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useTranslations } from "translations";
import ErrorPage from "../ErrorPage";
import { ErrorScreen } from "./ErrorScreen";
import {
  AdmissionsStepsProvider,
  useAdmissionsSteps,
} from "./Form/useAdmissionsSteps";
import { ADMISSIONS_INFO_SCREENS } from "./InfoScreens";
import { getAdmissionsForm } from "./shared";

type AdmissionsContextType = {
  admissionsId: string;
  admissionsPayload: CareproviderAdmissionPage;
  isPreview: boolean;
};

const AdmissionsContext = createContext<AdmissionsContextType | null>(null);

export function useAdmissionsContext() {
  const context = useContext(AdmissionsContext);
  if (!context) {
    throw new Error(
      "useAdmissionsContext must be used within AdmissionsContextProvider",
    );
  }
  return context;
}

export function AdmissionsContextProvider() {
  const translations = useTranslations();
  const { admissionsId } = useParams<{ admissionsId: string }>();

  const [queryProgress, admissionsPayload, { error }] =
    useGetCareProviderAdmissionsPage({
      admissionsId,
    });

  if (!admissionsId || error) {
    return (
      <ErrorScreen
        description={
          translations.careproviderApp.recareAdmissionsForm
            .errorDescriptionPageInactive
        }
        errorCode={
          translations.careproviderApp.recareAdmissionsForm
            .errorCodePageInactive
        }
        imageAlt={
          translations.careproviderApp.recareAdmissionsForm
            .errorAltTextPageInactive
        }
        title={
          translations.careproviderApp.recareAdmissionsForm
            .errorTitlePageInactive
        }
      />
    );
  }

  return (
    <Result
      data={admissionsPayload}
      id="admissions"
      queryProgress={queryProgress}
    >
      {(typedAdmissionsPayload) => {
        return (
          <AdmissionsContext.Provider
            value={{
              admissionsId,
              admissionsPayload: typedAdmissionsPayload,
              isPreview: false,
            }}
          >
            <AdmissionsStepsProvider>
              <Outlet />
            </AdmissionsStepsProvider>
          </AdmissionsContext.Provider>
        );
      }}
    </Result>
  );
}

const FALLBACK_LOGO = {
  file_name: "",
  file_type: FILE_TYPES.LOGO,
  id: -1,
  url: "",
};

export function AdmissionsPreviewContextProvider({
  careprovider,
  children,
}: {
  careprovider: Careprovider;
  children: React.ReactNode;
}) {
  const { form: formType } = useIntParams<{ form: AdmissionsFormType }>();

  const admissionForm = getAdmissionsForm(careprovider, formType);

  const [queryProgress, files] = useGetCareproviderFileList({
    careproviderId: careprovider.id,
  });

  if (!admissionForm?.uuid) {
    return (
      <ErrorPage
        getErrorLabel={(translations) =>
          translations.careproviderProfile.loadingError
        }
        pageName="Admissions preview - no form uuid"
      />
    );
  }

  return (
    <Result data={files} id="admissions-files" queryProgress={queryProgress}>
      {(typedFiles) => (
        <AdmissionsContext.Provider
          value={{
            admissionsId: admissionForm.uuid!,
            admissionsPayload: {
              careprovider,
              form_type: formType,
              id: careprovider.id,
              logo: typedFiles.logo ?? FALLBACK_LOGO,
            },
            isPreview: true,
          }}
        >
          <AdmissionsStepsProvider>
            <AdmissionsPreviewPatientContextProvider>
              {children}
            </AdmissionsPreviewPatientContextProvider>
          </AdmissionsStepsProvider>
        </AdmissionsContext.Provider>
      )}
    </Result>
  );
}

type AdmissionsPatientContextType = {
  auction: Auction | undefined;
};

const AdmissionsPatientContext =
  createContext<AdmissionsPatientContextType | null>(null);

export function useAdmissionsPatientContext() {
  const context = useContext(AdmissionsPatientContext);
  if (!context) {
    throw new Error(
      "useAdmissionsPatientContext must be used within AdmissionsPatientContextProvider",
    );
  }
  return context;
}

const useRouteGuard = () => {
  const { admissionsId } = useAdmissionsContext();
  const { currentStep } = useAdmissionsSteps();

  const navigation = useCareproviderNavigationHandlers();

  return useCallback(
    ({ auction }: { auction: Auction }) => {
      const assessmentCompleted = auction?.assessment_completed;

      const userOnInvalidStep =
        currentStep >
        (auction?.admissions?.step ?? ADMISSIONS_STEPS.PERSONAL_INFORMATION);

      if (assessmentCompleted) {
        setTimeout(() => {
          navigation.goToAdmissionsInfoScreens({
            admissionsId,
            infoType: ADMISSIONS_INFO_SCREENS.FORM_SUBMITTED_SUCCESS,
          });
        }, 0);
        return;
      }
      if (userOnInvalidStep) {
        setTimeout(() => {
          navigation.goToAdmissionsForm({
            admissionsId,
            step: ADMISSIONS_STEPS.PERSONAL_INFORMATION,
          });
        }, 0);
        return;
      }
    },
    [currentStep, navigation, admissionsId],
  );
};

export function AdmissionsPatientContextProvider() {
  const translations = useTranslations();
  const { admissionsId } = useAdmissionsContext();
  const routeGuard = useRouteGuard();

  const [queryProgress, auction, { error }] = useGetAdmissionsPatient({
    admissionsId,
    onCompleted: routeGuard,
  });

  if (!admissionsId || error) {
    return (
      <ErrorScreen
        description={translations.general.tryAgain}
        imageAlt={
          translations.careproviderApp.recareAdmissionsForm
            .errorAltTextPageInactive
        }
        title={translations.general.status.error}
      />
    );
  }

  if (isLoading(queryProgress)) {
    return (
      <SpinnerPageContainer>
        <SpinnerPage id="admissions-patient" />
      </SpinnerPageContainer>
    );
  }

  return (
    <PatientEncryptionProvider patient={auction?.patient}>
      <AdmissionsPatientContext.Provider value={{ auction }}>
        <Outlet />
      </AdmissionsPatientContext.Provider>
    </PatientEncryptionProvider>
  );
}

export function AdmissionsPreviewPatientContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <AdmissionsPatientContext.Provider value={{ auction: undefined }}>
      {children}
    </AdmissionsPatientContext.Provider>
  );
}
